import { useOutletContext, Link } from "react-router-dom";
import { Sparkles, Cpu, ArrowRight, Activity, Hand, Bluetooth, Brain } from "lucide-react";
import { Container, Reveal, siteImg } from "@/site/ui";

const ICONS = [Brain, Activity, Hand, Bluetooth];

export default function BionicProstheses() {
  const c = useOutletContext();
  const b = c.bionic || {};
  const features = b.features || [];
  const stats = b.stats || [];
  const gallery = b.gallery || [];

  return (
    <div data-testid="bionic-page" className="bg-[#060608]">
      {/* Hero */}
      <section className="relative overflow-hidden pt-[200px] pb-24 md:pb-28">
        <div className="absolute inset-0">
          {b.hero_image && <img src={siteImg(b.hero_image)} alt="" className="h-full w-full object-cover object-center opacity-50" />}
          <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_70%_20%,rgb(var(--rgh-primary-rgb)/0.35),transparent_60%)]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060608] via-[#060608]/85 to-[#060608]/40" />
        </div>
        <Container className="relative">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/80 backdrop-blur">
              <Sparkles size={14} className="text-[var(--rgh-primary)]" /> {b.eyebrow}
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]">
              {b.heading}
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-white/70">{b.subtitle}</p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to={b.cta_link || "/get-a-quote"} data-testid="bionic-hero-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-[var(--rgh-primary)] px-7 py-3.5 text-sm font-semibold text-white transition-[transform,background-color,box-shadow] duration-200 hover:bg-[var(--rgh-primary-dark)] hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgb(var(--rgh-primary-rgb)/0.5)]">
                {b.cta_text || "Book a consultation"} <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white/90 transition-colors duration-200 hover:bg-white/10">
                Talk to a specialist
              </Link>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Stats bar */}
      {stats.length > 0 && (
        <section className="border-y border-white/10">
          <Container className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.06} className="px-4 py-8 text-center">
                <p className="text-3xl md:text-4xl font-bold text-white">{s.value}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.16em] text-white/50">{s.label}</p>
              </Reveal>
            ))}
          </Container>
        </section>
      )}

      {/* Intro + features */}
      <section className="py-20 md:py-28">
        <Container>
          <Reveal>
            <div className="flex items-center gap-3 text-[var(--rgh-primary)]"><Cpu size={20} /><span className="text-xs font-semibold uppercase tracking-[0.2em]">How it works</span></div>
            <p className="mt-6 max-w-3xl text-xl md:text-2xl leading-relaxed text-white/85">{b.intro}</p>
          </Reveal>

          <div className="mt-14 grid sm:grid-cols-2 gap-5">
            {features.map((f, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <Reveal key={i} delay={(i % 2) * 0.08}>
                  <div data-testid={`bionic-feature-${i}`} className="group h-full rounded-3xl border border-white/10 bg-white/[0.03] p-7 transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-[rgb(var(--rgh-primary-rgb)/0.5)] hover:bg-white/[0.06]">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[rgb(var(--rgh-primary-rgb)/0.15)] text-[var(--rgh-primary)] transition-transform duration-300 group-hover:scale-110">
                      <Icon size={22} />
                    </span>
                    <h3 className="mt-5 text-lg font-semibold text-white">{f.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="pb-20 md:pb-28">
          <Container>
            <div className="grid sm:grid-cols-3 gap-4">
              {gallery.map((g, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="overflow-hidden rounded-3xl border border-white/10">
                    <img src={siteImg(g)} alt="" className="aspect-[3/4] w-full object-cover transition-transform duration-700 hover:scale-105" />
                  </div>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* CTA */}
      <section className="pb-24 md:pb-32">
        <Container>
          <Reveal>
            <div className="relative overflow-hidden rounded-[32px] border border-white/10 p-10 md:p-16 text-center">
              <div className="absolute inset-0 bg-[radial-gradient(50%_120%_at_50%_0%,rgb(var(--rgh-primary-rgb)/0.35),transparent_70%)]" />
              <div className="relative">
                <h2 className="mx-auto max-w-2xl text-3xl md:text-4xl font-bold tracking-tight text-white">{b.cta_title}</h2>
                <Link to={b.cta_link || "/get-a-quote"} data-testid="bionic-cta"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--rgh-primary)] px-8 py-4 text-sm font-semibold text-white transition-[transform,background-color,box-shadow] duration-200 hover:bg-[var(--rgh-primary-dark)] hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgb(var(--rgh-primary-rgb)/0.5)]">
                  {b.cta_text || "Book a consultation"} <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
